import type { CSSProperties } from 'react'
import { Link, Navigate } from '@tanstack/react-router'
import { roleAtLeast, roleSchema, type Role } from '@lms/shared-types'
import { useSession } from '../auth/auth-context'
import { Card } from '../components/primitives/Card'
import { PageHead } from '../components/primitives/PageHead'
import { ThinkingDots } from '../components/primitives/ThinkingDots'
import { flags } from '../lib/feature-flags'
import { HomeLobbyPreview, type HomeCardModel } from './home/HomeLobbyPreview'
import { EmployeeHomeDashboard } from './home/EmployeeHomeDashboard'
import { useHomeInsights } from '../lib/use-home-insights'
import type { HomeInsightCard } from '../api/home-insights'

/**
 * Default landing surface after sign-in.
 *
 *   - flag OFF (legacy): redirects each role to its primary destination
 *     (`/my-leave`, `/manager`, `/hr/employees`).
 *   - flag ON (`redesignedHome`): renders a thin role-aware lobby with
 *     a `PageHead` greeting + 2-3 navigation cards pointing at the next
 *     thing the user would do. No stat tiles or AI digest yet — those
 *     need real data and ship in a follow-up; rendering them empty
 *     would lie. See `docs/design/briefs/home.md` for the design ask.
 */

export function HomePage(): JSX.Element {
  const { user, isLoading } = useSession()

  if (isLoading) {
    return (
      <div style={{ padding: 24 }}>
        <ThinkingDots />
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" />
  }

  // Superadmins own the product config — land them on /admin by default
  // instead of the role lobby. (Admins still get the lobby + Admin card.)
  const userRoles = user.roles && user.roles.length > 0 ? user.roles : [user.role]
  if (flags.adminHub && userRoles.includes('superadmin')) {
    return <Navigate to="/admin" replace />
  }

  const parsed = roleSchema.safeParse(user.role)
  const role: Role = parsed.success ? parsed.data : 'employee'

  if (!flags.redesignedHome) {
    // Director / Exec — the escalation inbox folded into the unified Approvals
    // page, so land them there (the "For approvals" tab holds escalated-to-them
    // leaves), not on /manager or the retired /director/escalations.
    if (role === 'director' || role === 'exec_director') {
      return <Navigate to="/manager/requests" />
    }
    // HR — Employees admin is the primary surface.
    if (role === 'hr') {
      return <Navigate to="/hr/employees" />
    }
    if (roleAtLeast(role, 'manager')) {
      return <Navigate to="/manager" />
    }
    return <Navigate to="/my-leave" />
  }

  const firstName = user.name.split(' ')[0] ?? user.name

  // Employees get the real planning dashboard — hero balance, leave-type
  // grid, and quick-action cards. Home is now identical for EVERY role — each
  // person is an employee with their own balances/leaves, so they all get the
  // same personal dashboard. Role-specific surfaces live elsewhere: managers/
  // directors on /manager and /manager/requests; HOD division views on
  // /hod/overview. One home to maintain, no per-role variants.
  if (
    role === 'employee' ||
    role === 'manager' ||
    role === 'hod' ||
    role === 'director' ||
    role === 'exec_director' ||
    role === 'hr'
  ) {
    return <EmployeeHomeDashboard firstName={firstName} />
  }

  if (flags.homeAiCards) {
    return <AiLobby role={role} firstName={firstName} />
  }

  return <Lobby role={role} firstName={firstName} />
}

function mapApiCardToModel(c: HomeInsightCard): HomeCardModel {
  if (c.kind === 'action') {
    return {
      kind: 'action',
      eyebrow: c.eyebrow,
      title: c.title,
      body: c.body,
      href: c.href,
      primary: c.primary ?? false,
      ...(c.metric !== undefined ? { metric: c.metric } : {}),
    }
  }
  const VALID_AI_EYEBROWS = ['HEADS UP', 'PATTERN', 'POLICY'] as const
  const eyebrow = (VALID_AI_EYEBROWS as ReadonlyArray<string>).includes(c.eyebrow)
    ? (c.eyebrow as 'HEADS UP' | 'PATTERN' | 'POLICY')
    : 'HEADS UP'
  return {
    kind: 'ai',
    id: c.id,
    eyebrow,
    title: c.title,
    body: c.body,
    href: c.href,
    cta: c.cta ?? 'Open',
    generatedAt: c.generatedAt
      ? new Date(c.generatedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
      : '',
    ...(c.why ? { why: c.why } : {}),
  }
}

function AiLobby({ role, firstName }: Readonly<{ role: Role; firstName: string }>): JSX.Element {
  const { data, isLoading, isError } = useHomeInsights()
  const config = LOBBY_BY_ROLE[role]

  if (isLoading) {
    return (
      <div style={{ padding: 24 }}>
        <ThinkingDots />
      </div>
    )
  }
  if (isError || !data) {
    // Fall back to the static Lobby so the page is never broken on API failure.
    return <Lobby role={role} firstName={firstName} />
  }

  const cards: HomeCardModel[] = data.cards.map((c) => mapApiCardToModel(c))
  return (
    <HomeLobbyPreview
      eyebrow={config.eyebrow}
      firstName={firstName}
      subtitle={config.subtitle}
      cards={cards}
    />
  )
}

interface LobbyProps {
  role: Role
  firstName: string
}

interface LobbyCard {
  eyebrow: string
  title: string
  body: string
  to: string
  primary?: boolean
}

const EMPLOYEE_CARDS_BASE: ReadonlyArray<LobbyCard> = [
  { eyebrow: 'Next step', title: 'Apply for leave', body: 'Open the apply form with the calendar and balance read-out.', to: '/apply', primary: true },
  { eyebrow: 'Read', title: 'My leave', body: 'See your balances, history, and pending requests.', to: '/my-leave' },
  { eyebrow: 'Plan', title: 'Team calendar', body: 'See who is out this week and pick dates that keep coverage.', to: '/calendar' },
]

const EMPLOYEE_CARDS_WITH_CHAT: ReadonlyArray<LobbyCard> = [
  ...EMPLOYEE_CARDS_BASE,
  { eyebrow: 'New', title: 'Apply via chat', body: 'Describe your leave in plain language — AI drafts the details.', to: '/apply/chat' },
]

const LOBBY_BY_ROLE: Record<Role, { eyebrow: string; subtitle: string; cards: ReadonlyArray<LobbyCard> }> = {
  employee: {
    eyebrow: 'Personal',
    subtitle: 'A quick read of where you stand. Apply for new leave, or open your full history.',
    cards: EMPLOYEE_CARDS_BASE,
  },
  manager: {
    eyebrow: 'Manager',
    subtitle: 'Today on your team. Review pending requests, then check coverage.',
    cards: [
      { eyebrow: 'Next step', title: 'Review pending requests', body: 'Go to the queue with two priority lanes (Needs your eye / Routine).', to: '/manager/requests', primary: true },
      { eyebrow: 'Read', title: 'Leave patterns', body: 'AI-surfaced patterns across your team.', to: '/manager/patterns' },
    ],
  },
  hod: {
    eyebrow: 'HOD',
    subtitle: 'Division pressure at a glance. Open the overview, or step into a specific team.',
    cards: [
      { eyebrow: 'Next step', title: 'Overview', body: 'Coverage stress across teams in your division for the next 4 weeks.', to: '/hod/overview', primary: true },
      { eyebrow: 'Read', title: 'Team requests', body: 'Open the team-level approval queue.', to: '/manager/requests' },
    ],
  },
  hr: {
    eyebrow: 'HR',
    subtitle: 'Anomalies and policy work. Skim Insights, then triage.',
    cards: [
      { eyebrow: 'Next step', title: 'HR Insights', body: 'AI-assisted briefing of org-wide trends, anomalies, and active blackouts.', to: '/hr/insights', primary: true },
      { eyebrow: 'Read', title: 'Employees', body: 'Balances, manager mapping, and HR-side leave history.', to: '/hr/employees' },
    ],
  },
  director: {
    eyebrow: 'Director',
    subtitle: 'Only blackout-window requests reach you. Review and decide.',
    cards: [
      { eyebrow: 'Next step', title: 'Blackout exceptions', body: 'Requests auto-routed to you because they overlap an active blackout window.', to: '/manager/requests', primary: true },
    ],
  },
  exec_director: {
    eyebrow: 'Exec Director',
    subtitle: 'Company-wide visibility. Final policy authority. You see escalations from every division and all sensitive approvals.',
    cards: [
      { eyebrow: 'Escalations', title: 'Escalation queue', body: 'Blackout-window exceptions and sensitive approvals from every division.', to: '/manager/requests', primary: true },
    ],
  },
  // admin / superadmin are system-apex roles (white-label). Until the /admin
  // hub lands (Phase 4) they default to the company-wide exec view; an Admin
  // Console card is added to their lobby in Phase 4.
  admin: {
    eyebrow: 'Admin',
    subtitle: 'Company-wide visibility plus product configuration.',
    cards: [
      { eyebrow: 'Next step', title: 'Admin console', body: 'Branding, colour theme, user roles, and all product configuration in one place.', to: '/admin', primary: true },
    ],
  },
  superadmin: {
    eyebrow: 'Super Admin',
    subtitle: 'Company-wide visibility plus full product configuration.',
    cards: [
      { eyebrow: 'Next step', title: 'Admin console', body: 'Branding, colour theme, user roles, and all product configuration in one place.', to: '/admin', primary: true },
    ],
  },
}

function Lobby({ role, firstName }: Readonly<LobbyProps>): JSX.Element {
  const config = LOBBY_BY_ROLE[role]
  return (
    <>
      <PageHead
        eyebrow={config.eyebrow}
        title={`Welcome back, ${firstName}`}
        subtitle={config.subtitle}
      />
      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
        {config.cards.map((card) => (
          <LobbyCardView key={card.to} card={card} />
        ))}
      </div>
    </>
  )
}

interface LobbyCardViewProps {
  card: LobbyCard
}

function LobbyCardView({ card }: Readonly<LobbyCardViewProps>): JSX.Element {
  return (
    <Link to={card.to} style={{ textDecoration: 'none' }}>
      <Card
        interactive
        padding={20}
        {...(card.primary ? { style: { borderLeft: '3px solid var(--text)' } } : {})}
      >
        <div style={lobbyEyebrowStyle}>{card.eyebrow}</div>
        <div style={lobbyTitleStyle}>{card.title}</div>
        <div style={lobbyBodyStyle}>{card.body}</div>
      </Card>
    </Link>
  )
}

const lobbyEyebrowStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  color: 'var(--text-3)',
  marginBottom: 8,
}

const lobbyTitleStyle: CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontSize: 18,
  fontWeight: 500,
  letterSpacing: '-0.01em',
  color: 'var(--text)',
  marginBottom: 6,
}

const lobbyBodyStyle: CSSProperties = {
  fontSize: 13,
  lineHeight: 1.55,
  color: 'var(--text-2)',
}
